<template>
  <div class="uptime-heatmap">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <svg class="animate-spin w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <span class="ml-2 text-sm text-gray-500">Loading heatmap...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <p class="text-sm text-red-600">
        {{ error }}
      </p>
      <button @click="fetchData" class="mt-2 text-sm text-blue-600 hover:underline">
        Retry
      </button>
    </div>

    <!-- Heatmap Grid -->
    <div v-else class="heatmap-container">
      <!-- Month labels -->
      <div class="month-labels">
        <span
          v-for="month in monthLabels"
          :key="month.key"
          class="month-label"
          :style="{ width: month.width + 'px' }">
          {{ month.label }}
        </span>
      </div>

      <!-- Grid with day labels -->
      <div class="flex">
        <!-- Day labels -->
        <div class="flex flex-col justify-around pr-2 text-xs text-gray-500" style="width: 24px;">
          <span>Mon</span>
          <span />
          <span>Wed</span>
          <span />
          <span>Fri</span>
          <span />
          <span>Sun</span>
        </div>

        <!-- Heatmap cells -->
        <div class="heatmap-grid">
          <div
            v-for="(week, weekIndex) in weeks"
            :key="weekIndex"
            class="heatmap-week">
            <div
              v-for="(day, dayIndex) in week"
              :key="`${weekIndex}-${dayIndex}`"
              class="heatmap-cell"
              :style="{ background: getCellColor(day) }"
              :title="getCellTitle(day)"
              @mouseenter="showTooltip($event, day)"
              @mouseleave="hideTooltip" />
          </div>
        </div>
      </div>

    </div>

    <!-- Tooltip (outside heatmap-container to avoid overflow clipping) -->
    <div
      v-if="tooltip.visible"
      class="heatmap-tooltip"
      :style="{ top: tooltip.top + 'px', left: tooltip.left + 'px' }">
      <div class="font-medium">
        {{ tooltip.date }}
      </div>
      <div v-if="tooltip.hasData">
        <div>{{ tooltip.uptime }}% uptime</div>
        <div>{{ tooltip.checks }} checks</div>
        <div v-if="tooltip.avgResponse">
          {{ tooltip.avgResponse }}ms avg
        </div>
      </div>
      <div v-else class="text-gray-400">
        No data
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'UptimeHeatmap',

  props: {
    websiteId: {
      type: String,
      required: true
    },
    days: {
      type: Number,
      default: 90
    },
    apiUrl: {
      type: String,
      required: true
    }
  },

  setup(props) {
    const loading = ref(true)
    const error = ref(null)
    const data = ref({})

    const tooltip = ref({
      visible: false,
      top: 0,
      left: 0,
      date: '',
      uptime: 0,
      checks: 0,
      avgResponse: null,
      hasData: false
    })

    // Generate date range
    const dateRange = computed(() => {
      const dates = []
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      for (let i = props.days - 1; i >= 0; i--) {
        const date = new Date(today)
        date.setDate(date.getDate() - i)
        dates.push(date)
      }
      return dates
    })

    // Organize into weeks (columns)
    const weeks = computed(() => {
      const result = []
      let currentWeek = []

      // Pad the beginning to align with day of week
      const firstDate = dateRange.value[0]
      const firstDayOfWeek = firstDate.getDay() // 0 = Sunday

      // Adjust for Monday start (0 = Monday, 6 = Sunday)
      const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1

      for (let i = 0; i < adjustedFirstDay; i++) {
        currentWeek.push(null)
      }

      dateRange.value.forEach((date) => {
        const dayOfWeek = date.getDay()
        const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1

        if (adjustedDay === 0 && currentWeek.length > 0) {
          result.push(currentWeek)
          currentWeek = []
        }

        currentWeek.push(date)
      })

      // Add remaining days
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push(null)
        }
        result.push(currentWeek)
      }

      return result
    })

    // Month labels for the top axis
    const monthLabels = computed(() => {
      const labels = []
      let currentMonth = null
      let weekCount = 0

      weeks.value.forEach((week) => {
        const validDays = week.filter((d) => d !== null)
        if (validDays.length === 0) return

        const firstDay = validDays[0]
        const month = firstDay.toLocaleString('default', { month: 'short' })

        if (month !== currentMonth) {
          if (currentMonth !== null) {
            labels.push({ label: currentMonth, width: weekCount * 14, key: currentMonth + labels.length })
          }
          currentMonth = month
          weekCount = 1
        } else {
          weekCount++
        }
      })

      if (currentMonth !== null) {
        labels.push({ label: currentMonth, width: weekCount * 14, key: currentMonth + labels.length })
      }

      return labels
    })

    const formatDate = (date) => date.toISOString().split('T')[0]

    const getCellColor = (date) => {
      if (!date) return 'var(--bg-surface)'

      const dateKey = formatDate(date)
      const dayData = data.value[dateKey]

      // No data or no checks for this day
      if (!dayData || !dayData.total_checks || dayData.total_checks === 0) {
        return 'var(--bg-muted)'
      }

      const uptime = dayData.uptime_percentage

      if (uptime === null || uptime === undefined) return 'var(--bg-muted)'
      if (uptime >= 99.9) return 'var(--color-success-500)'
      if (uptime >= 99) return 'var(--color-success-400)'
      if (uptime >= 95) return 'var(--color-warning-400)'
      if (uptime >= 90) return 'var(--color-warning-500)'
      return 'var(--color-error-500)'
    }

    const getCellTitle = (date) => {
      if (!date) return ''

      const dateKey = formatDate(date)
      const dayData = data.value[dateKey]

      // No data or no checks for this day
      if (!dayData || !dayData.total_checks || dayData.total_checks === 0) {
        return `${date.toLocaleDateString()}: No data`
      }

      return `${date.toLocaleDateString()}: ${dayData.uptime_percentage?.toFixed(1)}% uptime, ${dayData.total_checks} checks`
    }

    const showTooltip = (event, date) => {
      if (!date) return

      const rect = event.target.getBoundingClientRect()
      const containerRect = event.target.closest('.uptime-heatmap').getBoundingClientRect()

      const dateKey = formatDate(date)
      const dayData = data.value[dateKey]

      // Only consider as "has data" if there are actual checks
      const hasRealData = dayData && dayData.total_checks > 0

      // Calculate position - show below if not enough space above
      const tooltipHeight = 70
      const cellTop = rect.top - containerRect.top
      const showBelow = cellTop < tooltipHeight

      tooltip.value = {
        visible: true,
        top: showBelow ? cellTop + rect.height + 8 : cellTop - tooltipHeight,
        left: rect.left - containerRect.left - 40,
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        uptime: hasRealData ? (dayData.uptime_percentage?.toFixed(1) || '0') : null,
        checks: hasRealData ? dayData.total_checks : 0,
        avgResponse: hasRealData && dayData.avg_response_time ? Math.round(dayData.avg_response_time) : null,
        hasData: hasRealData
      }
    }

    const hideTooltip = () => {
      tooltip.value.visible = false
    }

    const fetchData = async() => {
      loading.value = true
      error.value = null

      try {
        const response = await fetch(`${props.apiUrl}?days=${props.days}`, {
          headers: { 'Accept': 'application/json' }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch data')
        }

        data.value = await response.json()
      } catch (e) {
        console.error('[UptimeHeatmap] Error:', e)
        error.value = e.message || 'Failed to load heatmap data'
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      error,
      data,
      weeks,
      monthLabels,
      tooltip,
      getCellColor,
      getCellTitle,
      showTooltip,
      hideTooltip,
      fetchData
    }
  }
}
</script>

<style scoped>
.uptime-heatmap {
  position: relative;
  overflow: visible;
}

.heatmap-container {
  overflow-x: auto;
  overflow-y: visible;
}

.month-labels {
  display: flex;
  margin-bottom: 6px;
  padding-left: 32px;
  font-size: 11px;
  color: var(--text-tertiary);
  overflow: hidden;
}

.month-label {
  flex-shrink: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.heatmap-grid {
  display: flex;
  gap: 2px;
}

.heatmap-week {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.heatmap-cell {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  cursor: pointer;
  transition: transform 0.1s ease;
}

.heatmap-cell:hover {
  transform: scale(1.2);
  outline: 2px solid var(--text-secondary);
  outline-offset: 1px;
}

.heatmap-tooltip {
  position: absolute;
  z-index: 9999;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  background: var(--bg-surface);
  border: 1px solid var(--border-secondary);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  white-space: nowrap;
}
</style>
